/**
 * Copyright (c) 2021-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql, type TypedDocumentNode } from "@apollo/client";
import { skipToken, useQuery } from "@apollo/client/react";
import { createContext, type ReactNode, useMemo, useSyncExternalStore } from "react";
import type { GQLMyNdlaDataQuery, GQLMyNdlaPersonalDataFragmentFragment } from "../graphqlTypes";
import { getActiveSessionCookieClient, isActiveSession, subscribeToSession } from "../util/authHelpers";

interface AuthContextType {
  authenticated: boolean;
  authContextLoaded: boolean;
  user: GQLMyNdlaPersonalDataFragmentFragment | null;
  examLock: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  authenticated: false,
  authContextLoaded: true,
  user: null,
  examLock: false,
});

interface Props {
  children: ReactNode;
  initialValue?: string;
}

export const personalDataQueryFragment = gql`
  fragment MyNdlaPersonalDataFragment on MyNdlaPersonalData {
    __typename
    id
    username
    email
    displayName
    groups {
      id
      displayName
      isPrimarySchool
      parentId
    }
    organization
    favoriteSubjects
    role
    arenaEnabled
  }
`;

const myNdlaQuery: TypedDocumentNode<GQLMyNdlaDataQuery> = gql`
  query myNdlaData {
    examLockStatus {
      key
      value
    }
    personalData {
      ...MyNdlaPersonalDataFragment
    }
  }
  ${personalDataQueryFragment}
`;

const getSnapshot = () => {
  return isActiveSession(getActiveSessionCookieClient());
};

export const AuthenticationContext = ({ children }: Props) => {
  const authenticated = useSyncExternalStore(subscribeToSession, getSnapshot, () => undefined);

  const myNdlaData = useQuery(myNdlaQuery, typeof window === "undefined" || !authenticated ? skipToken : {});

  const authContextLoaded = useMemo(() => {
    return authenticated !== undefined && myNdlaData.loading === false;
  }, [authenticated, myNdlaData.loading]);

  const user = useMemo(() => {
    if (authenticated && myNdlaData.data) return myNdlaData.data.personalData;
    return null;
  }, [authenticated, myNdlaData.data]);

  const examLock = useMemo(() => {
    return (
      !!authenticated &&
      myNdlaData.data?.personalData?.role === "student" &&
      myNdlaData.data?.examLockStatus?.value === true
    );
  }, [authenticated, myNdlaData.data]);

  return (
    <AuthContext
      value={{
        authenticated: !!authenticated,
        authContextLoaded,
        user,
        examLock,
      }}
    >
      {children}
    </AuthContext>
  );
};
